import { FootprintPolygonsType, PositionType } from 'data/types';
import {
  EdgeCardinal,
  getExteriorPolygonEdges,
  SpacePolygonEdge
} from 'utils/space-polygon';
import {
  FlushCenterType,
  NearestWallSegmentType,
  WallSegmentEndpointOptionsType,
  WallSegmentProjectionType,
  WallSegmentSideType,
  WallSegmentSpanType,
  WallSegmentType
} from 'utils/wall-segment-types';

const ENDPOINT_EPS = 1e-6;
const PARALLEL_EPS = 1e-3;
const COLLINEAR_DISTANCE_EPS_FT = 0.01;

export function isHorizontalCardinal(
  cardinal: EdgeCardinal | null | undefined
): boolean {
  return cardinal === 'top' || cardinal === 'bottom';
}

export function outwardNormalForCardinal(cardinal: EdgeCardinal): PositionType {
  if (cardinal === 'top') return { x: 0, y: -1 };
  if (cardinal === 'bottom') return { x: 0, y: 1 };
  if (cardinal === 'left') return { x: -1, y: 0 };
  return { x: 1, y: 0 };
}

export function shouldReverseSegmentEndpoints(
  p1: PositionType,
  p2: PositionType,
  cardinal: EdgeCardinal | null
): boolean {
  const horizontal = cardinal === null || isHorizontalCardinal(cardinal);
  const primaryDelta = horizontal ? p2.x - p1.x : p2.y - p1.y;
  const secondaryDelta = horizontal ? p2.y - p1.y : p2.x - p1.x;
  if (Math.abs(primaryDelta) > ENDPOINT_EPS) {
    return primaryDelta < 0;
  }
  return secondaryDelta < 0;
}

export function wallSegmentFromEndpoints(
  p1: PositionType,
  p2: PositionType,
  options: WallSegmentEndpointOptionsType = {}
): WallSegmentType {
  const cardinal = options.cardinal ?? null;
  const reverse = shouldReverseSegmentEndpoints(p1, p2, cardinal);
  const start = reverse ? p2 : p1;
  const end = reverse ? p1 : p2;
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthFt = Math.hypot(dx, dy);
  const direction =
    lengthFt > 0 ? { x: dx / lengthFt, y: dy / lengthFt } : { x: 1, y: 0 };
  const outwardNormal =
    options.outwardNormal ??
    (cardinal
      ? outwardNormalForCardinal(cardinal)
      : { x: direction.y + 0, y: -direction.x + 0 });

  return {
    edgeIndex: options.edgeIndex ?? 0,
    start: { x: start.x, y: start.y },
    end: { x: end.x, y: end.y },
    lengthFt,
    direction,
    outwardNormal,
    angleRad: Math.atan2(direction.y, direction.x),
    cardinal,
    isExterior: options.isExterior ?? true
  };
}

export function wallSegmentFromEdge(edge: SpacePolygonEdge): WallSegmentType {
  return wallSegmentFromEndpoints(edge.p1, edge.p2, {
    edgeIndex: edge.edgeIndex,
    outwardNormal: edge.outward,
    cardinal: edge.cardinal,
    isExterior: edge.isExterior
  });
}

export function buildWallSegments(
  absoluteVertices: PositionType[],
  footprints: FootprintPolygonsType | null
): WallSegmentType[] {
  return getExteriorPolygonEdges(absoluteVertices, footprints).map(
    wallSegmentFromEdge
  );
}

export function exteriorWallSegments(
  absoluteVertices: PositionType[],
  footprints: FootprintPolygonsType | null
): WallSegmentType[] {
  return buildWallSegments(absoluteVertices, footprints).filter(
    (segment) => segment.isExterior
  );
}

export function translateWallSegment(
  segment: WallSegmentType,
  delta: PositionType
): WallSegmentType {
  return {
    ...segment,
    start: { x: segment.start.x + delta.x, y: segment.start.y + delta.y },
    end: { x: segment.end.x + delta.x, y: segment.end.y + delta.y }
  };
}

export function pointAlongSegment(
  segment: WallSegmentType,
  offsetFt: number
): PositionType {
  return {
    x: segment.start.x + segment.direction.x * offsetFt,
    y: segment.start.y + segment.direction.y * offsetFt
  };
}

export function segmentSpan(
  segment: WallSegmentType,
  offsetFt: number,
  widthFt: number
): WallSegmentSpanType {
  return {
    p1: pointAlongSegment(segment, offsetFt),
    p2: pointAlongSegment(segment, offsetFt + widthFt)
  };
}

export function signedDistanceToSegmentLine(
  segment: WallSegmentType,
  point: PositionType
): number {
  const vx = point.x - segment.start.x;
  const vy = point.y - segment.start.y;
  return vx * segment.outwardNormal.x + vy * segment.outwardNormal.y;
}

export function unclampedOffsetOnSegment(
  segment: WallSegmentType,
  point: PositionType
): number {
  const vx = point.x - segment.start.x;
  const vy = point.y - segment.start.y;
  return vx * segment.direction.x + vy * segment.direction.y;
}

export function projectPointOnWallSegment(
  segment: WallSegmentType,
  point: PositionType
): WallSegmentProjectionType {
  const offsetFt = Math.min(
    segment.lengthFt,
    Math.max(0, unclampedOffsetOnSegment(segment, point))
  );
  const projected = pointAlongSegment(segment, offsetFt);
  return {
    offsetFt,
    distanceFt: Math.hypot(point.x - projected.x, point.y - projected.y),
    point: projected
  };
}

export function nearestSegment(
  segments: WallSegmentType[],
  point: PositionType
): NearestWallSegmentType | null {
  let best: NearestWallSegmentType | null = null;
  for (const segment of segments) {
    const projection = projectPointOnWallSegment(segment, point);
    if (!best || projection.distanceFt < best.projection.distanceFt) {
      best = { segment, projection };
    }
  }
  return best;
}

export function clampCenterOffsetOnSegment(
  segment: WallSegmentType,
  offsetFt: number,
  sizeFt: number
): number {
  const half = sizeFt / 2;
  if (segment.lengthFt <= sizeFt) {
    return segment.lengthFt / 2;
  }
  return Math.min(segment.lengthFt - half, Math.max(half, offsetFt));
}

export function flushCenterOnSegment(
  segment: WallSegmentType,
  offsetFt: number,
  sizeFt: number,
  side: WallSegmentSideType
): FlushCenterType {
  const clampedOffsetFt = clampCenterOffsetOnSegment(segment, offsetFt, sizeFt);
  const onWall = pointAlongSegment(segment, clampedOffsetFt);
  const sign = side === 'inside' ? -1 : 1;
  const half = sizeFt / 2;
  return {
    center: {
      x: onWall.x + sign * segment.outwardNormal.x * half,
      y: onWall.y + sign * segment.outwardNormal.y * half
    },
    offsetFt: clampedOffsetFt
  };
}

export function segmentsAreCollinear(
  a: WallSegmentType,
  b: WallSegmentType
): boolean {
  const cross = a.direction.x * b.direction.y - a.direction.y * b.direction.x;
  if (Math.abs(cross) > PARALLEL_EPS) {
    return false;
  }
  const vx = b.start.x - a.start.x;
  const vy = b.start.y - a.start.y;
  const offLine = Math.abs(vx * a.direction.y - vy * a.direction.x);
  return offLine <= COLLINEAR_DISTANCE_EPS_FT;
}

export function segmentsCollinearOverlapFt(
  a: WallSegmentType,
  b: WallSegmentType
): number {
  if (!segmentsAreCollinear(a, b)) {
    return 0;
  }
  const t1 = unclampedOffsetOnSegment(a, b.start);
  const t2 = unclampedOffsetOnSegment(a, b.end);
  const from = Math.max(0, Math.min(t1, t2));
  const to = Math.min(a.lengthFt, Math.max(t1, t2));
  return Math.max(0, to - from);
}

export function normalizeRotationDeg(rotationDeg: number): number {
  const normalized = rotationDeg % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

export function rotationDegFromOutwardNormal(normal: PositionType): number {
  return normalizeRotationDeg(
    (Math.atan2(normal.x, -normal.y) * 180) / Math.PI
  );
}

export function segmentRotationDeg(segment: WallSegmentType): number {
  return rotationDegFromOutwardNormal(segment.outwardNormal);
}

export function rectWallSegments(
  widthFt: number,
  heightFt: number,
  origin: PositionType = { x: 0, y: 0 }
): WallSegmentType[] {
  const topLeft = origin;
  const topRight = { x: origin.x + widthFt, y: origin.y };
  const bottomRight = { x: origin.x + widthFt, y: origin.y + heightFt };
  const bottomLeft = { x: origin.x, y: origin.y + heightFt };
  return [
    wallSegmentFromEndpoints(topLeft, topRight, {
      edgeIndex: 0,
      cardinal: 'top'
    }),
    wallSegmentFromEndpoints(topRight, bottomRight, {
      edgeIndex: 1,
      cardinal: 'right'
    }),
    wallSegmentFromEndpoints(bottomRight, bottomLeft, {
      edgeIndex: 2,
      cardinal: 'bottom'
    }),
    wallSegmentFromEndpoints(bottomLeft, topLeft, {
      edgeIndex: 3,
      cardinal: 'left'
    })
  ];
}
